// ItemForm.js
import React, { useState, useEffect } from 'react';
import { FiSave, FiX, FiPackage, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './addItem.css';

const units = [
  { value: 'piece', label: 'Piece (1)' },
  { value: 'set', label: 'Set (6)' },
  { value: 'dozen', label: 'Dozen (12)' },
  { value: 'kg', label: 'Kg' }
];

const formatRs = (amount) =>
  'Rs. ' + Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const computeMargin = (costPrice, sellingPrice) => {
  const cost = parseFloat(costPrice);
  const selling = parseFloat(sellingPrice);
  if (!cost || cost <= 0 || isNaN(selling) || selling < 0 || costPrice === '' || sellingPrice === '') {
    return { status: 'none' };
  }
  const amount = Math.round((selling - cost) * 100) / 100;
  if (selling <= cost) {
    return { status: 'warning', amount };
  }
  const percent = Math.round((amount / cost) * 100);
  return { status: 'ok', amount, percent };
};

const emptyPurchaseEdit = () => ({
  quantity: '',
  unit: 'piece',
  costPrice: '',
  transportCost: '',
  purchaseDate: ''
});

const emptyRow = () => ({
  itemName: '',
  itemCode: '',
  supplier: '',
  categoryId: '',
  costPrice: '',
  sellingPrice: '',
  quantity: '',
  transportCost: '',
  unit: 'piece'
});

const ItemForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ---- Shared state (used by both edit and add modes) ----
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- Edit-mode-only state (single item) ----
  const [formData, setFormData] = useState({
    itemName: '',
    itemCode: '',
    supplier: '',
    sellingPrice: '',
    unit: 'piece'
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [errors, setErrors] = useState({});
  const [currentStock, setCurrentStock] = useState(0);

  // ---- Edit-mode-only state (purchase history) ----
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(isEdit);
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [purchaseEditData, setPurchaseEditData] = useState(emptyPurchaseEdit());
  const [purchaseEditErrors, setPurchaseEditErrors] = useState({});
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);

  // ---- Add-mode-only state (multiple rows) ----
  const [rows, setRows] = useState([emptyRow()]);
  const [batchDate, setBatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [rowErrors, setRowErrors] = useState({});
  const [rowResults, setRowResults] = useState(null); // per-row success/failure after submit
  const [rowAddingCategory, setRowAddingCategory] = useState({});
  const [rowNewCategoryName, setRowNewCategoryName] = useState({});
  const [collapsedRows, setCollapsedRows] = useState(new Set());
  const [expandedRowIndex, setExpandedRowIndex] = useState(0);

  const fetchPurchases = async () => {
    try {
      setPurchasesLoading(true);
      const res = await axios.get(`/items/${id}/purchases`);
      setPurchases(res.data);
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      setApiError(error.response?.data?.error || 'Failed to load purchase history.');
    } finally {
      setPurchasesLoading(false);
    }
  };

  // Fetch shared data + item data (edit mode) on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const suppliersRes = await axios.get('/suppliers');
        setSuppliers(suppliersRes.data);

        const categoriesRes = await axios.get('/category');
        setCategories(categoriesRes.data);

        if (isEdit) {
          const itemRes = await axios.get(`/items/item/${id}`);
          const item = itemRes.data;

          setFormData({
            itemName: item.item_name,
            itemCode: item.item_code || '',
            supplier: item.supplier_id,
            sellingPrice: item.selling_price,
            unit: item.unit || 'piece'
          });
          setCurrentStock(item.quantity);

          if (item.category_id) {
            const selectedCat = categoriesRes.data.find(cat => cat.category_id === item.category_id);
            if (selectedCat) setSelectedCategory(item.category_id);
          }

          await fetchPurchases();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setApiError(error.response?.data?.error || 'Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => setApiError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError]);

  // ================= EDIT MODE (single item) =================

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const validateSingleForm = () => {
    const newErrors = {};
    if (!formData.itemName.trim()) newErrors.itemName = 'Item name is required';
    if (!formData.sellingPrice) {
      newErrors.sellingPrice = 'Selling price is required';
    } else if (isNaN(formData.sellingPrice) || parseFloat(formData.sellingPrice) <= 0) {
      newErrors.sellingPrice = 'Selling price must be a positive number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    if (!validateSingleForm()) return;

    setIsSubmitting(true);
    try {
      const itemData = {
        item_name: formData.itemName,
        item_code: formData.itemCode || null,
        supplier_id: formData.supplier || null,
        category_id: selectedCategory || null,
        selling_price: parseFloat(formData.sellingPrice),
        unit: formData.unit
      };

      await axios.put(`/items/${id}`, itemData);
      navigate('/inventory/viewInventory');
    } catch (err) {
      console.error('Error:', err);
      setApiError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------- Edit mode: correct a single past purchase ----------------
  // Each purchase (the original stock-in, or any later restock) is edited on
  // its own — fixing one never touches the numbers recorded for another.

  const startEditPurchase = (purchase) => {
    setEditingPurchaseId(purchase.purchase_id);
    setPurchaseEditData({
      quantity: purchase.quantity,
      unit: purchase.unit || 'piece',
      costPrice: purchase.cost_price,
      transportCost: purchase.transport_cost || 0,
      purchaseDate: purchase.purchase_date
        ? new Date(purchase.purchase_date).toISOString().split('T')[0]
        : ''
    });
    setPurchaseEditErrors({});
  };

  const cancelEditPurchase = () => {
    setEditingPurchaseId(null);
    setPurchaseEditData(emptyPurchaseEdit());
    setPurchaseEditErrors({});
  };

  const updatePurchaseEditField = (field, value) => {
    setPurchaseEditData(prev => ({ ...prev, [field]: value }));
    if (purchaseEditErrors[field]) {
      setPurchaseEditErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validatePurchaseEdit = () => {
    const newErrors = {};
    if (
      purchaseEditData.quantity === '' ||
      !Number.isInteger(Number(purchaseEditData.quantity)) ||
      parseInt(purchaseEditData.quantity) < 1
    ) {
      newErrors.quantity = 'Must be a whole number, at least 1';
    }
    if (purchaseEditData.costPrice === '' || isNaN(purchaseEditData.costPrice) || parseFloat(purchaseEditData.costPrice) < 0) {
      newErrors.costPrice = 'Enter a valid cost price';
    }
    if (purchaseEditData.transportCost !== '' && (isNaN(purchaseEditData.transportCost) || parseFloat(purchaseEditData.transportCost) < 0)) {
      newErrors.transportCost = 'Enter a valid transport cost';
    }
    setPurchaseEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSavePurchaseEdit = async (purchaseId) => {
    setApiError(null);
    if (!validatePurchaseEdit()) return;

    setPurchaseSubmitting(true);
    try {
      const res = await axios.put(`/items/${id}/purchases/${purchaseId}`, {
        quantity: parseInt(purchaseEditData.quantity),
        unit: purchaseEditData.unit,
        cost_price: parseFloat(purchaseEditData.costPrice),
        transport_cost: parseFloat(purchaseEditData.transportCost) || 0,
        purchase_date: purchaseEditData.purchaseDate || undefined
      });

      setCurrentStock(res.data.item_quantity);
      cancelEditPurchase();
      await fetchPurchases();
    } catch (err) {
      console.error('Error updating purchase:', err);
      setApiError(err.response?.data?.error || 'Failed to update purchase. Please try again.');
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  // ================= ADD MODE (multiple rows) =================

  const updateRow = (index, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (rowErrors[index]?.[field]) {
      setRowErrors(prev => ({
        ...prev,
        [index]: { ...prev[index], [field]: null }
      }));
    }
  };

  const addRow = () => {
    setRows(prev => {
      const newRows = [...prev, emptyRow()];
      const newIndex = newRows.length - 1;
      collapseRowIfValid(expandedRowIndex);
      setExpandedRowIndex(newIndex);
      return newRows;
    });
  };

  const removeRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
    setRowErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setCollapsedRows(prev => {
      const next = new Set();
      prev.forEach(i => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    setExpandedRowIndex(prev => {
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const startAddCategoryForRow = (index) => {
    setRowAddingCategory(prev => ({ ...prev, [index]: true }));
    setRowNewCategoryName(prev => ({ ...prev, [index]: '' }));
  };

  const cancelAddCategoryForRow = (index) => {
    setRowAddingCategory(prev => ({ ...prev, [index]: false }));
    setRowNewCategoryName(prev => ({ ...prev, [index]: '' }));
  };

  const saveNewCategoryForRow = async (index) => {
    const name = (rowNewCategoryName[index] || '').trim();
    if (!name) return;
    try {
      const res = await axios.post('/category', { name });
      const newCategory = { category_id: res.data.category_id, name };
      setCategories(prev => [...prev, newCategory]);
      updateRow(index, 'categoryId', String(newCategory.category_id));
      cancelAddCategoryForRow(index);
    } catch (err) {
      console.error('Error adding category:', err);
      setApiError(err.response?.data?.error || 'Failed to add category');
    }
  };

  const isRowValid = (row) => {
    return (
      row.itemName.trim() !== '' &&
      row.costPrice !== '' && !isNaN(row.costPrice) && parseFloat(row.costPrice) > 0 &&
      row.sellingPrice !== '' && !isNaN(row.sellingPrice) && parseFloat(row.sellingPrice) > 0 &&
      row.quantity !== '' && Number.isInteger(Number(row.quantity)) && parseInt(row.quantity) >= 0
    );
  };

  const collapseRowIfValid = (index) => {
    const row = rows[index];
    if (!row) return;
    if (isRowValid(row) && !rowErrors[index]) {
      setCollapsedRows(prev => new Set(prev).add(index));
    }
  };

  const expandRow = (index) => {
    setCollapsedRows(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    setExpandedRowIndex(index);
  };

  const validateRows = () => {
    const newRowErrors = {};
    let hasError = false;

    rows.forEach((row, index) => {
      const errs = {};
      if (!row.itemName.trim()) errs.itemName = 'Required';
      if (!row.costPrice || isNaN(row.costPrice) || parseFloat(row.costPrice) <= 0) {
        errs.costPrice = 'Must be > 0';
      }
      if (!row.sellingPrice || isNaN(row.sellingPrice) || parseFloat(row.sellingPrice) <= 0) {
        errs.sellingPrice = 'Must be > 0';
      }
      // Quantity 0 is explicitly allowed — for stock you expect to receive later
      if (row.quantity === '' || row.quantity === null) {
        errs.quantity = 'Required (use 0 if not in stock yet)';
      } else if (!Number.isInteger(Number(row.quantity)) || parseInt(row.quantity) < 0) {
        errs.quantity = 'Must be a whole number, 0 or more';
      }

      if (Object.keys(errs).length > 0) {
        newRowErrors[index] = errs;
        hasError = true;
      }
    });

    setRowErrors(newRowErrors);
    return !hasError;
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    setRowResults(null);

    if (!validateRows()) {
      setCollapsedRows(new Set());
      return;
    }

    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        rows.map(row => {
          const quantity = parseInt(row.quantity);
          const isPlaceholder = quantity === 0;

          return axios.post('/items', {
            item_name: row.itemName,
            item_code: row.itemCode || null,
            supplier_id: row.supplier || null,
            category_id: row.categoryId || null,
            quantity,
            cost_price: parseFloat(row.costPrice),
            selling_price: parseFloat(row.sellingPrice),
            // No stock has arrived yet for a 0-quantity placeholder, so there's
            // nothing to allocate transport cost against — force it to 0.
            transport_cost: isPlaceholder ? 0 : (parseFloat(row.transportCost) || 0),
            unit: row.unit,
            created_at: batchDate
          });
        })
      );

      const summary = results.map((result, index) => ({
        itemName: rows[index].itemName,
        success: result.status === 'fulfilled',
        message: result.status === 'fulfilled'
          ? (result.value.data.message || 'Saved')
          : (result.reason.response?.data?.error || 'Failed to save')
      }));

      setRowResults(summary);

      const allSucceeded = summary.every(r => r.success);
      if (allSucceeded) {
        navigate('/inventory/viewInventory');
      } else {
        // Keep only the rows that failed, so the user can fix and resubmit just those
        const failedIndexes = summary
          .map((r, i) => (!r.success ? i : null))
          .filter(i => i !== null);
        setRows(prev => prev.filter((_, i) => failedIndexes.includes(i)));
      }
    } catch (err) {
      console.error('Unexpected bulk-add error:', err);
      setApiError('An unexpected error occurred while saving items.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading item data...</div>;
  }

  // ================= RENDER: EDIT MODE =================
  if (isEdit) {
    return (
      <div className="add-item-container">
        {apiError && (
          <div className="api-notification">
            {apiError}
            <button className="close-btn" onClick={() => setApiError(null)}>×</button>
          </div>
        )}

        <div className="add-item-header">
          <h1 className="add-item-title">Edit Item</h1>
          <button
            className="cancel-btn"
            onClick={() => navigate('/inventory/viewInventory')}
            disabled={isSubmitting}
          >
            <FiX /> Cancel
          </button>
        </div>

        <form onSubmit={handleEditSubmit} className="item-form">
          <div className={`form-group ${errors.itemName ? 'has-error' : ''}`}>
            <label htmlFor="itemName">
              <FiPackage className="input-icon" /> Item Name *
            </label>
            <input
              type="text"
              id="itemName"
              name="itemName"
              value={formData.itemName}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            {errors.itemName && <span className="error-message">{errors.itemName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="itemCode">Item Code</label>
            <input
              type="text"
              id="itemCode"
              name="itemCode"
              value={formData.itemCode}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="supplier">Supplier (Optional)</label>
            <select
              id="supplier"
              name="supplier"
              value={formData.supplier}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="">Select a supplier</option>
              {suppliers.map(supplier => (
                <option key={supplier.supplier_id} value={supplier.supplier_id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
              disabled={isSubmitting}
            >
              <option value="">No category</option>
              {categories.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className={`form-group ${errors.sellingPrice ? 'has-error' : ''}`}>
              <label htmlFor="sellingPrice">Selling Price (Rs) *</label>
              <input
                type="number" id="sellingPrice" name="sellingPrice"
                value={formData.sellingPrice} onChange={handleChange}
                step="0.01" min="0.01" disabled={isSubmitting}
                onWheel={(e) => e.target.blur()}
              />
              {errors.sellingPrice && <span className="error-message">{errors.sellingPrice}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="unit">Display Unit *</label>
              <select id="unit" name="unit" value={formData.unit} onChange={handleChange} disabled={isSubmitting}>
                {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              <FiSave /> {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div className="purchase-history-section">
          <div className="form-section-title">
            <h3>Purchase History</h3>
            <span className="current-stock-badge">Current stock: {currentStock} pcs</span>
          </div>

          {purchasesLoading ? (
            <div className="loading">Loading purchase history...</div>
          ) : purchases.length === 0 ? (
            <p className="hint-message">
              No purchases yet — this item has no stock on record. Use Restock (from the inventory list) to log the first delivery.
            </p>
          ) : (
            <div className="purchase-history-list">
              {purchases.map(purchase => (
                <div key={purchase.purchase_id} className="purchase-history-row">
                  {editingPurchaseId === purchase.purchase_id ? (
                    <div className="purchase-edit-form">
                      <div className="form-row">
                        <div className={`form-group ${purchaseEditErrors.quantity ? 'has-error' : ''}`}>
                          <label>Quantity *</label>
                          <input
                            type="number"
                            value={purchaseEditData.quantity}
                            onChange={(e) => updatePurchaseEditField('quantity', e.target.value)}
                            min="1"
                            disabled={purchaseSubmitting}
                            onWheel={(e) => e.target.blur()}
                          />
                          {purchaseEditErrors.quantity && <span className="error-message">{purchaseEditErrors.quantity}</span>}
                        </div>
                        <div className="form-group">
                          <label>Unit *</label>
                          <select
                            value={purchaseEditData.unit}
                            onChange={(e) => updatePurchaseEditField('unit', e.target.value)}
                            disabled={purchaseSubmitting}
                          >
                            {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className={`form-group ${purchaseEditErrors.costPrice ? 'has-error' : ''}`}>
                          <label>Cost Price (Rs) *</label>
                          <input
                            type="number"
                            value={purchaseEditData.costPrice}
                            onChange={(e) => updatePurchaseEditField('costPrice', e.target.value)}
                            step="0.01" min="0"
                            disabled={purchaseSubmitting}
                            onWheel={(e) => e.target.blur()}
                          />
                          {purchaseEditErrors.costPrice && <span className="error-message">{purchaseEditErrors.costPrice}</span>}
                        </div>
                        <div className={`form-group ${purchaseEditErrors.transportCost ? 'has-error' : ''}`}>
                          <label>Transport Cost (Rs)</label>
                          <input
                            type="number"
                            value={purchaseEditData.transportCost}
                            onChange={(e) => updatePurchaseEditField('transportCost', e.target.value)}
                            step="1" min="0"
                            disabled={purchaseSubmitting}
                            onWheel={(e) => e.target.blur()}
                          />
                          {purchaseEditErrors.transportCost && <span className="error-message">{purchaseEditErrors.transportCost}</span>}
                        </div>
                        <div className="form-group">
                          <label>Purchase Date</label>
                          <input
                            type="date"
                            value={purchaseEditData.purchaseDate}
                            onChange={(e) => updatePurchaseEditField('purchaseDate', e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                            disabled={purchaseSubmitting}
                          />
                        </div>
                      </div>
                      <div className="purchase-edit-actions">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={cancelEditPurchase}
                          disabled={purchaseSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="submit-btn"
                          onClick={() => handleSavePurchaseEdit(purchase.purchase_id)}
                          disabled={purchaseSubmitting}
                        >
                          <FiSave /> {purchaseSubmitting ? 'Saving...' : 'Save Purchase'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="purchase-history-details">
                        <span className="purchase-history-date">
                          {purchase.purchase_date ? new Date(purchase.purchase_date).toLocaleDateString() : '—'}
                        </span>
                        <span>{purchase.quantity} {purchase.unit}</span>
                        <span>{formatRs(purchase.cost_price)} / unit</span>
                        <span>Total: {formatRs(purchase.total_amount)}</span>
                      </div>
                      <button
                        type="button"
                        className="edit-purchase-btn"
                        onClick={() => startEditPurchase(purchase)}
                        disabled={editingPurchaseId !== null}
                      >
                        <FiEdit2 /> Edit
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ================= RENDER: ADD MODE (bulk rows) =================
  return (
    <div className="add-item-container">
      {apiError && (
        <div className="api-notification">
          {apiError}
          <button className="close-btn" onClick={() => setApiError(null)}>×</button>
        </div>
      )}

      <div className="add-item-header">
        <h1 className="add-item-title">Add New Items</h1>
        <button
          className="cancel-btn"
          onClick={() => navigate('/inventory/viewInventory')}
          disabled={isSubmitting}
        >
          <FiX /> Cancel
        </button>
      </div>

      <div className="form-group">
        <label htmlFor="batchDate">Date Created (applies to all items below)</label>
        <input
          type="date"
          id="batchDate"
          value={batchDate}
          onChange={(e) => setBatchDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          disabled={isSubmitting}
        />
      </div>

      <form onSubmit={handleBulkSubmit} className="item-form bulk-item-form">
        {rows.map((row, index) => (
          <div key={index} className="bulk-item-row">
            <div className="bulk-item-row-header">
              <h3>Item {index + 1}{row.itemName ? `: ${row.itemName}` : ''}</h3>
              {rows.length > 1 && (
                <button
                  type="button"
                  className="remove-row-btn"
                  onClick={() => removeRow(index)}
                  disabled={isSubmitting}
                >
                  <FiTrash2 /> Remove
                </button>
              )}
            </div>

            {collapsedRows.has(index) ? (
              <button
                type="button"
                className="bulk-item-row-summary"
                onClick={() => expandRow(index)}
              >
                <span className="summary-name">{row.itemName}</span>
                <span className="summary-detail">
                  {formatRs(row.sellingPrice)} · {row.quantity} {row.unit}
                </span>
                <FiEdit2 className="summary-edit-icon" />
              </button>
            ) : (
              <>
                <div className={`form-group ${rowErrors[index]?.itemName ? 'has-error' : ''}`}>
                  <label>
                    <FiPackage className="input-icon" /> Item Name *
                  </label>
                  <input
                    type="text"
                    value={row.itemName}
                    onChange={(e) => updateRow(index, 'itemName', e.target.value)}
                    placeholder="Enter item name"
                    disabled={isSubmitting}
                  />
                  {rowErrors[index]?.itemName && <span className="error-message">{rowErrors[index].itemName}</span>}
                </div>

                <div className="form-group">
                  <label>Item Code</label>
                  <input
                    type="text"
                    value={row.itemCode}
                    onChange={(e) => updateRow(index, 'itemCode', e.target.value)}
                    placeholder="Enter item code"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Supplier (Optional)</label>
                    <select
                      value={row.supplier}
                      onChange={(e) => updateRow(index, 'supplier', e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.supplier_id} value={supplier.supplier_id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    {rowAddingCategory[index] ? (
                      <div className="inline-category-create">
                        <input
                          type="text"
                          value={rowNewCategoryName[index] || ''}
                          onChange={(e) => setRowNewCategoryName(prev => ({ ...prev, [index]: e.target.value }))}
                          placeholder="New category name"
                          disabled={isSubmitting}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="inline-category-save"
                          onClick={() => saveNewCategoryForRow(index)}
                          disabled={isSubmitting || !(rowNewCategoryName[index] || '').trim()}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="inline-category-cancel"
                          onClick={() => cancelAddCategoryForRow(index)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <select
                        value={row.categoryId}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            startAddCategoryForRow(index);
                          } else {
                            updateRow(index, 'categoryId', e.target.value);
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        <option value="">No category</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                        ))}
                        <option value="__new__">+ New category…</option>
                      </select>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className={`form-group ${rowErrors[index]?.costPrice ? 'has-error' : ''}`}>
                    <label>Cost Price (Rs) *</label>
                    <input
                      type="number"
                      value={row.costPrice}
                      onChange={(e) => updateRow(index, 'costPrice', e.target.value)}
                      step="0.01" min="0.01" placeholder="0.00"
                      disabled={isSubmitting}
                      onWheel={(e) => e.target.blur()}
                    />
                    {rowErrors[index]?.costPrice && <span className="error-message">{rowErrors[index].costPrice}</span>}
                  </div>

                  <div className={`form-group ${rowErrors[index]?.sellingPrice ? 'has-error' : ''}`}>
                    <label>Selling Price (Rs) *</label>
                    <input
                      type="number"
                      value={row.sellingPrice}
                      onChange={(e) => updateRow(index, 'sellingPrice', e.target.value)}
                      step="0.01" min="0.01" placeholder="0.00"
                      disabled={isSubmitting}
                      onWheel={(e) => e.target.blur()}
                    />
                    {rowErrors[index]?.sellingPrice && <span className="error-message">{rowErrors[index].sellingPrice}</span>}
                    {(() => {
                      const margin = computeMargin(row.costPrice, row.sellingPrice);
                      if (margin.status === 'ok') {
                        return (
                          <span className="margin-feedback margin-ok">
                            Margin: {formatRs(margin.amount)} ({margin.percent}%)
                          </span>
                        );
                      }
                      if (margin.status === 'warning') {
                        return (
                          <span className="margin-feedback margin-warning">
                            ⚠ Selling price is at or below cost
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className={`form-group ${rowErrors[index]?.quantity ? 'has-error' : ''}`}>
                    <label>Quantity *</label>
                    <input
                      type="number"
                      value={row.quantity}
                      onChange={(e) => updateRow(index, 'quantity', e.target.value)}
                      min="0" placeholder="0 if not in stock yet"
                      disabled={isSubmitting}
                      onWheel={(e) => e.target.blur()}
                    />
                    {rowErrors[index]?.quantity && <span className="error-message">{rowErrors[index].quantity}</span>}
                    {row.quantity === '0' && (
                      <span className="hint-message">Placeholder item — use Restock later to log the first delivery when stock arrives.</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Unit *</label>
                    <select
                      value={row.unit}
                      onChange={(e) => updateRow(index, 'unit', e.target.value)}
                      disabled={isSubmitting}
                    >
                      {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Transport Cost (Rs)</label>
                    <input
                      type="number"
                      value={row.transportCost}
                      onChange={(e) => updateRow(index, 'transportCost', e.target.value)}
                      step="1" min="0" placeholder="0.00"
                      disabled={isSubmitting || row.quantity === '0'}
                      onWheel={(e) => e.target.blur()}
                      onBlur={() => collapseRowIfValid(index)}
                    />
                    {row.quantity === '0' && (
                      <span className="hint-message">Not applicable until stock arrives.</span>
                    )}
                  </div>
                </div>

                {rowResults?.[index] && !rowResults[index].success && (
                  <div className="api-error-message">{rowResults[index].message}</div>
                )}
              </>
            )}
          </div>
        ))}

        <div className="add-row-btn-container">
          <button type="button" className="add-row-btn" onClick={addRow} disabled={isSubmitting}>
            <FiPlus /> Add Another Item
          </button>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            <FiSave /> {isSubmitting ? 'Saving Items...' : `Save ${rows.length > 1 ? `All ${rows.length} Items` : 'Item'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItemForm;